import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Inbox, RefreshCw, Loader2, Mail } from "lucide-react";
import ReplyCard from "@/components/ReplyCard";

export default function RepliesPage() {
  const [tab, setTab] = useState(() => localStorage.getItem("vv:replies:tab") || "pending");
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ status: "idle" });
  const [polling, setPolling] = useState(false);
  const [backfillDays, setBackfillDays] = useState(0);

  useEffect(() => { localStorage.setItem("vv:replies:tab", tab); }, [tab]);

  const fetchReplies = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inbox/replies?status=${status}`);
      setReplies(data.replies || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar respuestas");
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const { data } = await api.get("/inbox/status");
      setSyncStatus(data);
      if (data.status !== "running") {
        setPolling(false);
        if (data.status === "done") {
          const r = data.last_result || {};
          toast.success(`Sincronización completada — ${r.processed ?? 0} correos de proveedor procesados`);
          fetchReplies();
        } else if (data.status === "error") {
          toast.error(`Sincronización falló: ${data.error}`);
        }
      }
    } catch (e) { /* reintenta */ }
  };

  useEffect(() => { fetchReplies(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/inbox/status").then(({ data }) => {
      setSyncStatus(data);
      if (data.status === "running") setPolling(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!polling) return undefined;
    const id = setInterval(fetchSyncStatus, 4000);
    return () => clearInterval(id);
  }, [polling]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSync = async () => {
    try {
      await api.post("/inbox/sync", { backfill_days: Number(backfillDays) || 0 });
      toast.info("Sincronización iniciada (solo lectura)…");
      setSyncStatus({ status: "running" });
      setPolling(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar");
    }
  };

  const onReviewed = (id) => setReplies((rs) => rs.filter((r) => r.id !== id));
  const onUpdated = (nr) => setReplies((rs) => rs.map((r) => (r.id === nr.id ? nr : r)));

  return (
    <div className="space-y-5" data-testid="replies-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="vv-label">Etapa 2 — Respuestas</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Inbox size={28} className="text-blue-600" />
            Respuestas de proveedores
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            El sistema lee el buzón de <b>heidi@viajadverdad.com</b> cada 15 min (L-V, 9:00-18:00 Madrid),
            detecta respuestas de proveedores y la IA extrae los datos. <b>Nada se aplica a Sofi sin tu
            revisión</b> — tú decides con &quot;Aplicar&quot; o &quot;Descartar&quot;.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <select
            value={backfillDays}
            onChange={(e) => setBackfillDays(e.target.value)}
            className="h-9 text-xs border border-gray-200 rounded-md px-2 bg-white"
            data-testid="replies-backfill-select"
            title="Además del correo nuevo, repescar los últimos N días"
          >
            <option value={0}>Solo correo nuevo</option>
            <option value={2}>Repescar 2 días</option>
            <option value={4}>Repescar 4 días</option>
            <option value={7}>Repescar 7 días</option>
          </select>
          <Button onClick={startSync} disabled={syncStatus.status === "running"}
            className="bg-blue-600 hover:bg-blue-700" data-testid="replies-sync">
            {syncStatus.status === "running"
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Sincronizando…</>
              : <><RefreshCw size={14} className="mr-1" /> Sincronizar ahora</>}
          </Button>
        </div>
      </div>

      {syncStatus.status === "running" && (
        <div className="vv-card p-4 border-l-4 border-blue-500 bg-blue-50/50 flex items-center gap-3" data-testid="replies-sync-banner">
          <Loader2 size={20} className="animate-spin text-blue-600" />
          <div>
            <div className="font-medium text-blue-900">Leyendo buzón (solo lectura)…</div>
            <div className="text-xs text-blue-700">{syncStatus.progress || "Iniciando…"}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[["pending", "Pendientes"], ["applied", "Aplicadas"], ["dismissed", "Descartadas"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            data-testid={`replies-tab-${k}`}>
            {label}
          </button>
        ))}
        <button onClick={() => fetchReplies(tab)} className="ml-auto px-3 text-gray-400 hover:text-gray-600" title="Refrescar" data-testid="replies-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {replies.length === 0 ? (
        <div className="vv-card p-12 text-center text-gray-500" data-testid="replies-empty">
          <Mail size={48} className="mx-auto mb-3 text-gray-300" />
          <p>No hay respuestas {tab === "pending" ? "pendientes" : tab === "applied" ? "aplicadas" : "descartadas"}.</p>
          {tab === "pending" && (
            <p className="text-xs mt-2 text-gray-400">
              El worker revisa el buzón cada 15 minutos en horario laboral. También puedes pulsar &quot;Sincronizar ahora&quot;.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {replies.map((r) => (
            <ReplyCard key={`${r.id}-${r.associated_at || ""}`} reply={r} tab={tab} onReviewed={onReviewed} onUpdated={onUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}
